import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCurrentActivity } from '@/hooks/use-current-activity'
import { hasAnyGps } from '@/lib/terrain'
import { selectSelectedActivity, useActivityStore } from '@/store/activity-store'

export interface TerrainLoadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * The disclosure shown before the app makes its one kind of optional
 * network request for activity-derived data: terrain tiles from
 * tiles.mapterhorn.com. Nothing is fetched until the user confirms here.
 * Opened from the timeline header's "Load terrain elevation" button and
 * from the Edit menu.
 */
export function TerrainLoadDialog({ open, onOpenChange }: TerrainLoadDialogProps) {
  const loaded = useActivityStore(selectSelectedActivity)
  const loadTerrainForSelected = useActivityStore((s) => s.loadTerrainForSelected)
  const { result } = useCurrentActivity(open ? loaded?.history : undefined)

  if (loaded == null) return null

  const hasGps = result == null ? null : hasAnyGps(result.activity.records)

  const handleConfirm = () => {
    // Progress, cancel and errors are shown in the timeline header (and a
    // toast on failure) -- the dialog's job ends at consent.
    void loadTerrainForSelected()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="terrain-load-description" className="max-w-lg">
        <DialogTitle>Load terrain elevation</DialogTitle>
        <DialogDescription id="terrain-load-description">
          Requests terrain map tiles for this route&apos;s area from tiles.mapterhorn.com. Your GPS
          track itself is not sent.
        </DialogDescription>

        <div className="text-muted-foreground flex flex-col gap-2 text-sm">
          <p>
            Only tile coordinates (zoom/x/y) are requested, the same way a map background is loaded.
            The result is shown as a separate &ldquo;Terrain elevation&rdquo; line next to your
            device&apos;s altitude for comparison; it is not saved and does not change your file
            unless you later choose &ldquo;Apply terrain elevation to file…&rdquo; in the Edit menu.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              A terrain model gives the ground surface: bridges, tunnels and indoor sections will be
              wrong.
            </li>
            <li>
              In countries without high-resolution data (for example Greece or Hungary) only 30 m
              data is available, and a barometric device is often more accurate.
            </li>
          </ul>
          {hasGps === false && (
            <p role="alert" className="text-destructive">
              This activity has no GPS positions, so there is no route to look up terrain for.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={hasGps !== true} onClick={handleConfirm}>
            Load terrain tiles
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
